<template>
    <div v-if="value != null">
        <div style="display: flex; gap: 15px;">
            <div
                v-if="value.value.type == 'rgba'"
                style="flex-grow: 0px; width: 55px; height: 55px; position: relative; border: 1px solid rgba(0,0,0,0.3);"
            >
                <div class="checker" />

                <div
                    class="swatch"
                    :style="style_color_swatch"
                />
            </div>

            <v-select
                v-if="['object-fit', 'background-size'].includes(value.name)"
                class="flex-grow-1 mt-0 mb-6"
                v-model="value.value.raw_value"
                :items="object_fit_options"
                :label="formatLabel('Image fit')"
                hide-details
                placeholder="Select image fit"
                @change="onChangeWithoutDialog"
            />

            <m-image-selector
                v-else-if="value.name === 'background-image'"
                class="flex-grow-1 my-0"
                :value="bg_image_without_css_url_wrapper"
                @input="onImageSelected"
                :label="formatLabel('Custom background image')"
            />

            <v-select
                v-else-if="['background-position-y', 'align-self', 'align-items'].includes(value.name)"
                class="flex-grow-1 mt-0 mb-6"
                v-model="value.value.raw_value"
                :items="vertical_position_options"
                :label="formatLabel('Image vertical alignment')"
                hide-details
                placeholder="Select image vertical alignment"
                @change="onChangeWithoutDialog"
            />

            <v-select
                v-else-if="value.name === 'background-position-x'"
                class="flex-grow-1 mt-0 mb-6"
                v-model="value.value.raw_value"
                :items="horizontal_position_options"
                :label="formatLabel('Image horizontal alignment')"
                hide-details
                placeholder="Select image horizontal alignment"
                @change="onChangeWithoutDialog"
            />

            <v-row
                v-else-if="['object-position', 'background-position'].includes(value.name)"
                class="mb-1"
            >
                <v-col
                    cols="12"
                    md="6"
                >
                    <v-select
                        class="flex-grow-1 mt-0 mb-1"
                        :value="vertical_position_base"
                        :items="vertical_position_options"
                        :label="formatLabel('Vertical alignment')"
                        hide-details
                        placeholder="Select vertical alignment"
                        @change="onChangeVerticalPosition"
                    />
                    <v-text-field
                        v-if="vertical_custom_active"
                        class="mt-2"
                        type="number"
                        min="0"
                        max="100"
                        hide-details
                        v-model="custom_vertical_percentage"
                        :label="formatLabel('Custom vertical percentage')"
                        placeholder="Enter vertical percentage"
                        @input="debouncedOnCustomVertical"
                    />
                </v-col>

                <v-col
                    cols="12"
                    md="6"
                >
                    <v-select
                        class="flex-grow-1 mt-0 mb-1"
                        :value="horizontal_position_base"
                        :items="horizontal_position_options"
                        :label="formatLabel('Horizontal alignment')"
                        hide-details
                        placeholder="Select horizontal alignment"
                        @change="onChangeHorizontalPosition"
                    />
                    <v-text-field
                        v-if="horizontal_custom_active"
                        class="mt-2"
                        type="number"
                        min="0"
                        max="100"
                        hide-details
                        v-model="custom_horizontal_percentage"
                        :label="formatLabel('Custom horizontal percentage')"
                        placeholder="Enter horizontal percentage"
                        @input="debouncedOnCustomHorizontal"
                    />
                </v-col>
            </v-row>

            <v-select
                v-else-if="value.name === 'aspect-ratio'"
                class="flex-grow-1 mt-0 mb-6"
                :value="normalizeAspectRatio(value.value.raw_value)"
                :items="aspect_ratio_options"
                :label="formatLabel('Height')"
                hide-details
                placeholder="Select image aspect ratio"
                @change="onChangeAspectRatio"
            >
                <span
                    slot="append-outer"
                    style="display: flex; gap: 8px; align-items: center;"
                >
                    <v-icon
                        @click="onClickDelete"
                        color="error"
                        style="pointer-events: auto;"
                        medium
                    >
                        mdi-delete
                    </v-icon>
                </span>
            </v-select>

            <v-text-field
                v-else-if="!excluded_properties.includes(value.name)"
                :label="formatLabel(value.name)"
                :value="value.value.raw_value"
                readonly
            >
                <span
                    slot="append-outer"
                    style="display: flex; gap: 8px; align-items: center;"
                >
                    <v-icon
                        @click="onClickEdit"
                        color="primary"
                        style="pointer-events: auto;"
                    >
                        mdi-lead-pencil
                    </v-icon>
                    <v-icon
                        @click="onClickDelete"
                        color="error"
                        style="pointer-events: auto;"
                        medium
                    >
                        mdi-delete
                    </v-icon>
                </span>
            </v-text-field>

            <m-edit-css-property-dialog
                ref="edit-css-property-dialog"
                @modify="$emit('modify', $event)"
            />
        </div>

    </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
    props: {
        value: {
            type: Object,
            default: function () { return null; }
        },

        excluded_properties: {
            type: Array,
            default: function () {
                return [
                    'display',
                    'grid-template-columns',
                    'grid-template-rows',
                    'grid-template-areas',
                    'grid-auto-columns',
                    'grid-auto-rows',
                    'grid-auto-flow',
                    'grid-column',
                    'grid-row',
                    'grid-area',
                    'background-repeat',
                    'background-repeat-x',
                    'background-repeat-y',
                ];
            }
        },
    },

    data() {
        return {
            aspect_ratio_options: [
                { text: 'Shortest', value: '5/1' },
                { text: 'Shorter', value: '4/1' },
                { text: 'Short', value: '3/1' },
                { text: 'Medium', value: '21/9' },
                { text: 'Taller', value: '2/1' },
                { text: 'Tallest', value: '16/9' },
            ],
            vertical_position_options: [
                { text: 'Top', value: 'top' },
                { text: 'Center', value: 'center' },
                { text: 'Bottom', value: 'bottom' },
                { text: 'Custom', value: 'custom' },
            ],
            horizontal_position_options: [
                { text: 'Left', value: 'left' },
                { text: 'Center', value: 'center' },
                { text: 'Right', value: 'right' },
                { text: 'Custom', value: 'custom' },
            ],
            object_fit_options: [
                { text: 'Zoom out to show entire image', value: 'contain' },
                { text: 'Zoom in to fill container', value: 'cover' },
            ],
            custom_vertical_percentage: '',
            custom_horizontal_percentage: '',
            vertical_custom_active: false,
            horizontal_custom_active: false,
        }
    },

    created() {
        this.debouncedOnCustomVertical = debounce(this.onCustomVerticalPercentageChange, 300);
        this.debouncedOnCustomHorizontal = debounce(this.onCustomHorizontalPercentageChange, 300);
    },
    beforeDestroy() {
        if (this.debouncedOnCustomVertical && this.debouncedOnCustomVertical.cancel) {
            this.debouncedOnCustomVertical.cancel();
        }
        if (this.debouncedOnCustomHorizontal && this.debouncedOnCustomHorizontal.cancel) {
            this.debouncedOnCustomHorizontal.cancel();
        }
    },
    mounted() {
        this.initCustomPercentageState();
    },

    methods: {
        initCustomPercentageState() {
            const name = this.value && this.value.name;
            if (!['object-position', 'background-position'].includes(name)) return;
            const raw = this.value.value.raw_value || '';
            const parts = raw.trim().split(/\s+/);
            // vertical part is parts[1]
            if (parts[1] && parts[1].endsWith('%')) {
                this.custom_vertical_percentage = parts[1].slice(0, -1);
                this.vertical_custom_active = true;
            }
            // horizontal part is parts[0]
            if (parts[0] && parts[0].endsWith('%')) {
                this.custom_horizontal_percentage = parts[0].slice(0, -1);
                this.horizontal_custom_active = true;
            }
        },
        normalizeAspectRatio(ratio) {
            if (!ratio || typeof ratio !== 'string') {
                return '';
            }

            // remove spaces and normalize format to 'x/y'
            return ratio.replace(/\s+/g, '');
        },

        wrapImageInCssUrl(imageUrl) {
            if (!imageUrl) {
                return null;
            }
            return `url(${imageUrl})`;
        },

        onImageSelected(newVal) {
            if (!newVal) {
                this.value.value.raw_value = "";
                this.onChangeWithoutDialog();
                return;
            }
            this.value.value.raw_value = this.wrapImageInCssUrl(newVal);
            this.onChangeWithoutDialog();
        },

        onChangeAspectRatio(newVal) {
            this.value.value.raw_value = this.normalizeAspectRatio(newVal);
            this.onChangeWithoutDialog();
        },

        formatLabel(label) {
            return label
                .replace(/-/g, ' ')
                .replace(/\b\w/g, char => char.toUpperCase());
        },

        onChangeVerticalPosition(newVal) {
            if (newVal === 'custom') {
                // pre-populate custom vertical % based on previous setting
                const prev = this.vertical_position_from_multi_axis;
                const map = { top: 0, center: 50, bottom: 100 };
                const def = map[prev] !== undefined ? map[prev] : 50;
                this.custom_vertical_percentage = def;
                this.vertical_custom_active = true;
                const horizontal = this.horizontal_position_from_multi_axis || 'center';
                this.value.value.raw_value = `${horizontal} ${def}%`;
                this.onChangeWithoutDialog();
                return;
            }
            this.vertical_custom_active = false;
            const horizontal = this.horizontal_position_from_multi_axis || 'center';
            this.value.value.raw_value = `${horizontal} ${newVal}`;
            this.onChangeWithoutDialog();
        },

        onChangeHorizontalPosition(newVal) {
            if (newVal === 'custom') {
                // pre-populate custom horizontal % based on previous setting
                const prev = this.horizontal_position_from_multi_axis;
                const map = { left: 0, center: 50, right: 100 };
                const def = map[prev] !== undefined ? map[prev] : 50;
                this.custom_horizontal_percentage = def;
                this.horizontal_custom_active = true;
                const vertical = this.vertical_position_from_multi_axis || 'center';
                this.value.value.raw_value = `${def}% ${vertical}`;
                this.onChangeWithoutDialog();
                return;
            }
            this.horizontal_custom_active = false;
            const vertical = this.vertical_position_from_multi_axis || 'center';
            this.value.value.raw_value = `${newVal} ${vertical}`;
            this.onChangeWithoutDialog();
        },

        onCustomVerticalPercentageChange() {
            const value = this.custom_vertical_percentage;
            const isValid = this.validatePercentage(value);
            if (!isValid) {
                return;
            }
            const horizontal = this.horizontal_position_from_multi_axis || 'center';
            this.value.value.raw_value = `${horizontal} ${value}%`;
            this.onChangeWithoutDialog();
        },

        onCustomHorizontalPercentageChange() {
            const value = this.custom_horizontal_percentage;
            const isValid = this.validatePercentage(value);
            if (!isValid) {
                return;
            }
            const vertical = this.vertical_position_from_multi_axis || 'center';
            this.value.value.raw_value = `${value}% ${vertical}`;
            this.onChangeWithoutDialog();
        },

        validatePercentage(value) {
            const num = parseFloat(value);
            return !isNaN(num) && num >= 0 && num <= 100;
        },

        onChangeWithoutDialog() {
            this.$emit('modify', {
                'op': 'set-property',
                'property_name': this.value.name,
                'value': this.value.value.raw_value
            });
        },

        onClickEdit() {
            this.$refs['edit-css-property-dialog'].showDialog({
                'label': this.value.name,
                'value': this.value
            });
        },

        onClickDelete() {
            this.$rc.confirm({
                'title': 'Remove CSS Property',
                'body': `Remove the "${this.value.name}" property?<br/><br/>This cannot be undone.`,
                'btn_text_ok': 'Remove',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.$emit('modify', {
                        'op': 'delete',
                        'property_name': this.value.name
                    });
                },
            });
        },
    },

    computed: {
        bg_image_without_css_url_wrapper() {
            // will come in as e.g. `url(image_url)` and returns just `image_url`
            if (this.value.value.raw_value) {
                let match = this.value.value.raw_value.match(/url\((['"]?)(.*?)\1\)/);
                if (match && match[2]) {
                    return match[2];
                }
            }
            return null;
        },

        style_color_swatch() {
            return 'background-color: ' + this.value.value.raw_value;
        },

        vertical_position_from_multi_axis() {
            if (!this.value.value.raw_value || typeof this.value.value.raw_value !== 'string') {
                return 'center';
            }
            const parts = this.value.value.raw_value.trim().split(/\s+/);
            return parts[1] || 'center';
        },

        horizontal_position_from_multi_axis() {
            if (!this.value.value.raw_value || typeof this.value.value.raw_value !== 'string') {
                return 'center';
            }
            const parts = this.value.value.raw_value.trim().split(/\s+/);
            return parts[0] || 'center';
        },

        vertical_position_base() {
            const raw = this.value && this.value.value.raw_value;
            if (!raw || typeof raw !== 'string') return 'center';
            const parts = raw.trim().split(/\s+/);
            const vert = parts[1] || 'center';
            const opts = this.vertical_position_options.map(o => o.value);
            return opts.includes(vert) && vert !== 'custom' ? vert : 'custom';
        },

        horizontal_position_base() {
            const raw = this.value && this.value.value.raw_value;
            if (!raw || typeof raw !== 'string') return 'center';
            const parts = raw.trim().split(/\s+/);
            const hor = parts[0] || 'center';
            const opts = this.horizontal_position_options.map(o => o.value);
            return opts.includes(hor) && hor !== 'custom' ? hor : 'custom';
        }
    },

    watch: {
        'value.value.raw_value': function (newRaw) {
            // react to external raw value changes and initialize custom percentage fields
            this.initCustomPercentageState();
        },
        vertical_position_base(val) {
            if (val !== 'custom') {
                this.vertical_custom_active = false;
                this.custom_vertical_percentage = '';
            }
        },
        horizontal_position_base(val) {
            if (val !== 'custom') {
                this.horizontal_custom_active = false;
                this.custom_horizontal_percentage = '';
            }
        }
    },
}
</script>

<style lang="scss" scoped>
.checker {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    background-image: url('https://files.redchirp.com/dcf69ef7-2e39-4bcd-b0ca-cd4be32eeff2.webp');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    z-index: 1;
}

.swatch {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    z-index: 2;
}
</style>